import { useCallback, useEffect, useState } from "react";
import { Laptop, Loader2, Download, Hammer, ExternalLink, CheckCircle2, AlertTriangle, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";

const INPUT = "w-full bg-[#0b0811] border border-white/10 rounded-lg px-3 py-2 text-sm text-white";
const STATUS = { queued: ["En attente", "text-slate-400"], building: ["Compilation…", "text-[#f7c935]"], done: ["Publié", "text-emerald-400"], error: ["Erreur", "text-red-400"] };

export default function AppsBuilder({ api, societeId, config, setBranding }) {
  const [apps, setApps] = useState(null);
  const slug = config.slug || "";
  const load = useCallback(() => api.get(`/societes/${societeId}/apps`).then((r) => setApps(r.data.apps)).catch(() => {}), [societeId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const active = apps && Object.values(apps).some((a) => ["queued", "building"].includes(a.job?.status));
    if (!active) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [apps, load]);

  const build = async (app) => {
    try {
      await api.post(`/societes/${societeId}/apps/build`, { app });
      toast.success("Génération lancée — 2 à 4 minutes");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Lancement impossible"); }
  };

  return (
    <div className="space-y-4" data-testid="builder-panel-apps">
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 space-y-2">
        <label className="text-xs font-bold text-slate-300 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> URL de la plateforme (version déployée)</label>
        <input value={config.branding.platform_url || ""} onChange={(e) => setBranding("platform_url", e.target.value)} placeholder="https://votre-domaine.fr — vide = adresse actuelle + écran « Serveur » au 1er lancement" className={INPUT} data-testid="builder-platform-url" />
        <p className="text-[11px] text-slate-500">Intégrée aux installateurs générés ci-dessous. Enregistrez le Builder avant de générer. Page de connexion de l'app : <b className="text-slate-300">/login/{slug || "…"}</b>.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        {apps ? Object.entries(apps).map(([key, a]) => {
          const st = a.job ? STATUS[a.job.status] || STATUS.queued : null;
          const busy = ["queued", "building"].includes(a.job?.status);
          return (
            <div key={key} className="bg-[#14101c] border border-white/5 rounded-xl p-4 flex flex-col gap-3" data-testid={`societe-app-${key}`}>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: config.branding.background || "#202020" }}>
                  {config.branding.logo_url ? <img src={config.branding.logo_url} alt="" className="w-8 h-8 object-contain" /> : <Laptop className="w-5 h-5" style={{ color: config.branding.primary }} />}
                </div>
                <div>
                  <div className="text-sm font-bold text-white">{config.societe_name} {a.label}</div>
                  <div className="text-[11px] text-slate-500">Windows 64 bits · icône = logo de la société</div>
                </div>
              </div>
              {a.job && (
                <div className={`text-xs flex items-center gap-1.5 ${st[1]}`} data-testid={`societe-app-${key}-status`}>
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : a.job.status === "done" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                  <span>{st[0]} — {a.job.step}</span>
                </div>
              )}
              {a.release && (
                <a href={a.release.download_url} className="text-xs text-emerald-400 hover:underline flex items-center gap-1" data-testid={`societe-app-${key}-download`}>
                  <Download className="w-3.5 h-3.5" /> {a.release.filename} · v{a.release.version} · {(a.release.size / 1048576).toFixed(0)} Mo
                </a>
              )}
              <Button onClick={() => build(key)} disabled={busy} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold h-9" data-testid={`societe-app-${key}-build`}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Hammer className="w-4 h-4 mr-1" />} {a.release ? "Régénérer" : "Générer l'installateur"}
              </Button>
            </div>
          );
        }) : <Loader2 className="w-5 h-5 animate-spin text-slate-500" />}
      </div>
      <a href={`/telechargements/${slug}`} target="_blank" rel="noreferrer" className="text-xs text-[#f7c935] hover:underline inline-flex items-center gap-1" data-testid="societe-apps-downloads-link">
        <ExternalLink className="w-3.5 h-3.5" /> Page de téléchargement de la société : /telechargements/{slug || "…"}
      </a>
    </div>
  );
}
